// Push: web (prohlížeč, PWA) a nativní aplikace (Capacitor, FCM/APNs). Docs/SPEC.md 2.8.
// Web: service worker (src/sw.ts) zprávu zobrazí, tady se řeší jen odběr přes PushManager.
// Nativně: plugin @capacitor/push-notifications dá token zařízení, zprávu zobrazí systém.
// Nativní plugin se načítá dynamicky až za isNative(), takže do webového bundlu nejde.
import type { PluginListenerHandle } from '@capacitor/core'
import { isNative } from './native'
import { nativeNotificationTarget } from './notificationTarget'

export type PushPermission = 'default' | 'granted' | 'denied'

export interface PushSubscriptionData {
  endpoint: string
  keys: { p256dh: string; auth: string }
}

/** VAPID klíč přichází jako base64url, pushManager chce bajty. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** Nativně push podporuje vždy (nepotřebuje service worker ani PushManager). */
export function isPushSupported(): boolean {
  return isNative() || isWebPushSupported()
}

/** Webové oprávnění; nativní se zjišťuje asynchronně (getNativePermission), do té doby 'default'. */
export function getPermission(): PushPermission {
  if (isNative()) return 'default'
  if (typeof Notification === 'undefined') return 'denied'
  return Notification.permission
}

export async function requestPermission(): Promise<PushPermission> {
  if (typeof Notification === 'undefined') return 'denied'
  return Notification.requestPermission()
}

function toData(sub: PushSubscription): PushSubscriptionData {
  const json = sub.toJSON()
  const p256dh = json.keys?.p256dh
  const auth = json.keys?.auth
  if (!json.endpoint || !p256dh || !auth) throw new Error('Odběr push nemá klíče.')
  return { endpoint: json.endpoint, keys: { p256dh, auth } }
}

/** Přihlásí toto zařízení k push (použije existující odběr, jinak vytvoří nový). */
export async function subscribePush(publicKey: string): Promise<PushSubscriptionData> {
  const registration = await navigator.serviceWorker.ready
  const existing = await registration.pushManager.getSubscription()
  const sub =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }))
  return toData(sub)
}

/** Aktuální odběr tohoto zařízení nebo null. */
export async function getCurrentSubscription(): Promise<PushSubscriptionData | null> {
  if (isNative() || !isWebPushSupported()) return null
  const registration = await navigator.serviceWorker.ready
  const sub = await registration.pushManager.getSubscription()
  return sub ? toData(sub) : null
}

/** Zruší odběr v prohlížeči. Vrací true, když nějaký byl. */
export async function unsubscribePush(): Promise<boolean> {
  if (isNative() || !isWebPushSupported()) return false
  const registration = await navigator.serviceWorker.ready
  const sub = await registration.pushManager.getSubscription()
  return sub ? sub.unsubscribe() : false
}

// ---------------------------------------------------------------------------------------------
// Nativní push (Capacitor)
// ---------------------------------------------------------------------------------------------

/** Plugin se načte až při prvním použití v nativní aplikaci (ve webu se vůbec nestahuje). */
// Vrací modul, ne samotný plugin: proxy pluginu v Capacitoru nesmí projít přes await/then.
function loadPushModule() {
  return import('@capacitor/push-notifications')
}

/** Jak dlouho čekáme na token od FCM/APNs po register(). */
export const NATIVE_REGISTER_TIMEOUT_MS = 15_000

export type NativePushOutcome =
  { ok: true; token: string } | { ok: false; reason: 'denied' | 'dismissed' | 'error' }

function mapNativePermission(state: string): PushPermission {
  if (state === 'granted') return 'granted'
  if (state === 'denied') return 'denied'
  return 'default'
}

/** Aktuální oprávnění k upozorněním v telefonu (bez dotazu na uživatele). */
export async function getNativePermission(): Promise<PushPermission> {
  if (!isNative()) return 'denied'
  try {
    const { PushNotifications } = await loadPushModule()
    return mapNativePermission((await PushNotifications.checkPermissions()).receive)
  } catch {
    return 'default'
  }
}

/**
 * Zaregistruje zařízení u FCM/APNs a vrátí token. S `prompt` se zeptá na oprávnění, bez něj
 * (tichá synchronizace po startu) jen tehdy, když už je uděleno.
 */
export async function registerNativePush(
  options: { prompt: boolean } = { prompt: true },
): Promise<NativePushOutcome> {
  if (!isNative()) return { ok: false, reason: 'error' }
  try {
    const { PushNotifications } = await loadPushModule()
    let state = mapNativePermission((await PushNotifications.checkPermissions()).receive)
    if (state !== 'granted') {
      if (state === 'denied' || !options.prompt) {
        return { ok: false, reason: state === 'denied' ? 'denied' : 'dismissed' }
      }
      state = mapNativePermission((await PushNotifications.requestPermissions()).receive)
      if (state !== 'granted') {
        return { ok: false, reason: state === 'denied' ? 'denied' : 'dismissed' }
      }
    }

    const handles: PluginListenerHandle[] = []
    let timer: ReturnType<typeof setTimeout> | undefined
    const outcome = new Promise<NativePushOutcome>((resolve) => {
      timer = setTimeout(() => resolve({ ok: false, reason: 'error' }), NATIVE_REGISTER_TIMEOUT_MS)
      // Posluchače se přidávají před register(), jinak by událost s tokenem proběhla naprázdno.
      void (async () => {
        try {
          handles.push(
            await PushNotifications.addListener('registration', (token) =>
              resolve(
                token.value ? { ok: true, token: token.value } : { ok: false, reason: 'error' },
              ),
            ),
          )
          handles.push(
            await PushNotifications.addListener('registrationError', () =>
              resolve({ ok: false, reason: 'error' }),
            ),
          )
          await PushNotifications.register()
        } catch {
          resolve({ ok: false, reason: 'error' })
        }
      })()
    })
    try {
      return await outcome
    } finally {
      clearTimeout(timer)
      await Promise.all(handles.map((h) => h.remove().catch(() => undefined)))
    }
  } catch {
    return { ok: false, reason: 'error' }
  }
}

/** Zruší registraci zařízení u FCM/APNs (po odebrání zařízení v Nastavení). */
export async function unregisterNativePush(): Promise<void> {
  if (!isNative()) return
  try {
    const { PushNotifications } = await loadPushModule()
    await PushNotifications.unregister()
  } catch {
    /* nic k rušení */
  }
}

export interface NativePushHandlers {
  /** Zpráva dorazila, zatímco je aplikace v popředí. */
  onReceived?: () => void
  /** Uživatel klepl na notifikaci (nebo její tlačítko): cesta v aplikaci. */
  onOpen?: (path: string) => void
}

/** Poslouchá nativní push. Vrací funkci, která posluchače odebere. Ve webu nedělá nic. */
export async function listenNativePush(handlers: NativePushHandlers): Promise<() => void> {
  if (!isNative()) return () => undefined
  const handles: PluginListenerHandle[] = []
  try {
    const { PushNotifications } = await loadPushModule()
    handles.push(
      await PushNotifications.addListener('pushNotificationReceived', () =>
        handlers.onReceived?.(),
      ),
    )
    handles.push(
      await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
        const path = nativeNotificationTarget(event.actionId, event.notification?.data)
        if (path) handlers.onOpen?.(path)
      }),
    )
  } catch {
    /* plugin není k dispozici */
  }
  return () => {
    for (const h of handles) void h.remove().catch(() => undefined)
  }
}
